import * as Sentry from "@sentry/react";

import { extractReportTitle } from "../../features/coaching/utils/prompts";
import { logError, normalizeError } from "../../utils/error";
import { invokeClaudeStream } from "./apiClient";
import {
  type BackendCoachingInsights,
  type CoachingInsightsDeltaEvent,
  type CoachingSection,
  consumeStream
} from "./coachingInsightsStream";
import { PRODUCTION_MODEL } from "./types";

export type { CoachingSection, CoachingInsightsDeltaEvent };

export interface CoachingInsights {
  title: string;
  sections: CoachingSection[];
}

export interface CoachingContext {
  agentPerformanceAnalysis?: string;
  lensDefinition?: string;
  teamBestPractices?: string;
}

export interface GenerateCoachingInsightsParams {
  coachingPrompt: string;
  systemPrompt: string;
  context: CoachingContext;
  agentName?: string;
  lensName: string;
  timeRange: string;
  temperature?: number;
  maxTokens?: number;
  model?: string;
  coachingPromptTitle?: string;
  onDelta?: (event: CoachingInsightsDeltaEvent) => void;
}

export interface GenerateCoachingInsightsResult {
  ok: boolean;
  insights?: CoachingInsights;
  error?: Error;
}

type FailureStage = "stream_error" | "no_result" | "exception";

interface FailureContext {
  stage: FailureStage;
  params: GenerateCoachingInsightsParams;
  accumulatorTextLength?: number;
  accumulatorJsonLength?: number;
  accumulatorThinkingLength?: number;
}

const readErrorMessage = async (response: Response): Promise<string> => {
  try {
    const errorData = await response.json();
    return errorData.error || "Failed to generate coaching insights";
  } catch {
    return `Failed to generate coaching insights (Status: ${response.status})`;
  }
};

const buildInsights = (
  backend: BackendCoachingInsights,
  params: GenerateCoachingInsightsParams
): CoachingInsights => {
  const fallbackTitle = params.coachingPromptTitle || params.lensName;
  const title = extractReportTitle({ prompt: params.coachingPrompt, fallbackTitle });
  return { title, sections: backend.sections };
};

const captureFailure = (error: Error, context: FailureContext): void => {
  logError(`Coaching insights failed (${context.stage})`, error);

  Sentry.captureException(error, {
    tags: {
      function: "generateCoachingInsights",
      module: "claude-coaching-insights",
      coaching_failure_stage: context.stage
    },
    extra: {
      hasCoachingPrompt: !!context.params.coachingPrompt,
      hasAgentPerformanceAnalysis: !!context.params.context.agentPerformanceAnalysis,
      hasLensDefinition: !!context.params.context.lensDefinition,
      hasTeamBestPractices: !!context.params.context.teamBestPractices,
      lensName: context.params.lensName,
      timeRange: context.params.timeRange,
      model: context.params.model,
      accumulatorTextLength: context.accumulatorTextLength,
      accumulatorJsonLength: context.accumulatorJsonLength,
      accumulatorThinkingLength: context.accumulatorThinkingLength
    }
  });
};

export const generateCoachingInsights = async (
  params: GenerateCoachingInsightsParams
): Promise<GenerateCoachingInsightsResult> => {
  try {
    const response = await invokeClaudeStream("generate-coaching-insights", {
      query: params.coachingPrompt,
      systemPrompt: params.systemPrompt,
      context: params.context,
      agentName: params.agentName,
      lensName: params.lensName,
      timeRange: params.timeRange,
      temperature: params.temperature ?? 0.1,
      maxTokens: params.maxTokens ?? 4000,
      model: params.model ?? PRODUCTION_MODEL.id
    });

    if (!response.ok) {
      const error = new Error(await readErrorMessage(response));
      captureFailure(error, { stage: "stream_error", params });
      return { ok: false, error };
    }

    const accumulator = await consumeStream(response, params.onDelta);

    if (accumulator.error) {
      const error = new Error(accumulator.error);
      captureFailure(error, {
        stage: "stream_error",
        params,
        accumulatorTextLength: accumulator.textBuffer.length,
        accumulatorJsonLength: accumulator.jsonBuffer.length,
        accumulatorThinkingLength: accumulator.thinkingBuffer.length
      });
      return { ok: false, error };
    }

    if (!accumulator.result) {
      const error = new Error("Coaching insights stream ended without a result");
      captureFailure(error, {
        stage: "no_result",
        params,
        accumulatorTextLength: accumulator.textBuffer.length,
        accumulatorJsonLength: accumulator.jsonBuffer.length,
        accumulatorThinkingLength: accumulator.thinkingBuffer.length
      });
      return { ok: false, error };
    }

    return { ok: true, insights: buildInsights(accumulator.result, params) };
  } catch (error) {
    const normalized = normalizeError(error);
    captureFailure(normalized, { stage: "exception", params });
    return { ok: false, error: normalized };
  }
};
